import {
   busyLocalKeyFromStored,
   localKeyFromTs,
   MOLDOVA_TZ_ID,
} from "../../components/APanel/CalendarExtreme/extremeTimezone";
import { readRawDateTimeParts } from "../../utils/moldovaTime";

export function localKeyFromStoredRaw(value) {
   const raw = readRawDateTimeParts(value);
   if (!raw) return "";
   return `${raw.year}-${raw.month}-${raw.day}|${raw.hour}:${raw.minute}`;
}

/**
 * Resolves API blackout datetimes to the same wall-clock slot key used by
 * Calendar Extreme / reservations: "YYYY-MM-DD|HH:mm".
 *
 * Create uses reservation local-match ISO (isoForDbMatchLocalHour), e.g. grid
 * 10:00 → "…T13:00:00+03:00". After DB round-trip that often becomes Instant
 * "…T10:00:00.000Z" where the **UTC digits are the grid hour**.
 *
 * Indexing must therefore prefer busyLocalKeyFromStored (same as reservations)
 * for Instant `.000Z` and for `+offset` payloads — NOT Moldova-from-UTC, which
 * would paint 10:00 at 13:00 and leave the real slot uncolored while the popup
 * still shows "Deblochează".
 *
 * Legacy wall-clock writes ("…T10:00:00Z" without ms) still resolve via digits.
 */
export function resolveBlackoutLocalKey(rawValue, options = {}) {
   const raw = String(rawValue || "").trim();
   if (!raw) return "";

   const validHmSet =
      options.validHmSet instanceof Set ? options.validHmSet : null;
   const isValidKey = (key) => {
      const hm = String(key || "").split("|")[1] || "";
      if (!hm) return false;
      if (validHmSet && validHmSet.size > 0) return validHmSet.has(hm);
      return true;
   };

   const rawKey = localKeyFromStoredRaw(raw);
   const hasNumericOffset = /[+-]\d{2}:\d{2}$/.test(raw);
   const hasZSuffix = /Z$/i.test(raw);
   const looksLikeInstantUtc = /\.\d{3}Z$/i.test(raw);
   const parsed = new Date(raw);
   const hasParsed = !Number.isNaN(parsed.getTime());
   const directKey = hasParsed
      ? localKeyFromTs(parsed.getTime(), MOLDOVA_TZ_ID)
      : "";
   const shiftedKey = hasParsed ? busyLocalKeyFromStored(raw) : "";

   // Reservation local-match create uses isoForDbMatchLocalHour, e.g. grid
   // 10:00 → "…T13:00:00+03:00". busyLocalKey recovers the grid hour.
   if (hasNumericOffset) {
      if (shiftedKey && isValidKey(shiftedKey)) return shiftedKey;
      if (rawKey && isValidKey(rawKey)) return rawKey;
      if (directKey && isValidKey(directKey)) return directKey;
      return shiftedKey || rawKey || directKey;
   }

   if (hasZSuffix) {
      if (looksLikeInstantUtc) {
         // DB Instant from local-match storage: digits == grid hour via busy key.
         // Moldova-from-UTC (directKey) would shift 10:00 → 13:00 and break paint.
         if (shiftedKey && isValidKey(shiftedKey)) return shiftedKey;
         if (rawKey && isValidKey(rawKey)) return rawKey;
         if (directKey && isValidKey(directKey)) return directKey;
         return shiftedKey || rawKey || directKey;
      }
      // Legacy wall-clock Z create ("…T10:00:00Z") — labeled hour wins.
      if (rawKey && isValidKey(rawKey)) return rawKey;
      if (shiftedKey && isValidKey(shiftedKey)) return shiftedKey;
      if (directKey && isValidKey(directKey)) return directKey;
      return rawKey || shiftedKey || directKey;
   }

   if (shiftedKey && isValidKey(shiftedKey)) return shiftedKey;
   if (rawKey && isValidKey(rawKey)) return rawKey;
   if (directKey && isValidKey(directKey)) return directKey;
   return shiftedKey || rawKey || directKey;
}
